import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../utils/firebase";
import { FaBars, FaTimes } from "react-icons/fa";
import "../styles/Navbar.css";
import logo from "../assets/images/sacs-logo.png";

const Navbar = () => {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    setDashboardOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
        setServicesOpen(false);
        setDashboardOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeAllMenus = useCallback(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    setDashboardOpen(false);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    closeAllMenus();
    navigate("/login");
  };

  return (
    <>
      <header className="nav-container" ref={navRef}>
        <div className="nav-inner">
          <div
            className="nav-logo"
            role="button"
            tabIndex={0}
            onClick={() => setLogoModalOpen(true)}
            aria-label="Open logo"
          >
            <img src={logo} alt="SACS Logo" className="logo-img" />
            <span className="logo-text">SACS</span>
          </div>

          <button
            className="nav-toggle"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <nav className={`nav-links ${menuOpen ? "open" : ""}`}>
            <Link to="/" onClick={closeAllMenus}>Home</Link>

            <div className="nav-dropdown">
              <div className="dropdown-toggle-with-caret">
                <button
                  className="dropdown-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/services");
                    closeAllMenus();
                  }}
                  aria-expanded={servicesOpen}
                >
                  Services
                </button>

                <button
                  className="dropdown-caret always-show"
                  onClick={(e) => {
                    e.stopPropagation();
                    setServicesOpen((prev) => !prev);
                    setDashboardOpen(false);
                  }}
                  aria-label="Toggle services menu"
                >
                  ?
                </button>
              </div>

              {servicesOpen && (
                <div className="dropdown-menu" role="menu">
                  <Link to="/catering" onClick={closeAllMenus}>Catering</Link>
                  <Link to="/chef-booking" onClick={closeAllMenus}>Madi Samayal</Link>
                  <Link to="/cloud-kitchen" onClick={closeAllMenus}>Daily Foods</Link>
                </div>
              )}
            </div>

            <Link to="/contact" onClick={closeAllMenus}>Contact</Link>

            {user ? (
              <>
                <div className="nav-dropdown">
                  <div className="dropdown-toggle-with-caret">
                    <button
                      className="dropdown-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/dashboard");
                        closeAllMenus();
                      }}
                      aria-expanded={dashboardOpen}
                    >
                      Dashboard
                    </button>

                    <button
                      className="dropdown-caret always-show"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDashboardOpen((prev) => !prev);
                        setServicesOpen(false);
                      }}
                      aria-label="Toggle dashboard menu"
                    >
                      ?
                    </button>
                  </div>

                  {dashboardOpen && (
                    <div className="dropdown-menu dashboard-menu" role="menu">
                      <Link to="/dashboard" onClick={closeAllMenus}>My Bookings</Link>
                      <Link to="/dashboard/profile" onClick={closeAllMenus}>Profile</Link>
                    </div>
                  )}
                </div>

                <button className="nav-logout" onClick={handleLogout}>
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={closeAllMenus}>Login</Link>
                <Link to="/register" onClick={closeAllMenus}>Register</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {logoModalOpen && (
        <div className="logo-modal-overlay" onClick={() => setLogoModalOpen(false)}>
          <div className="logo-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={logo} alt="SACS Logo Large" className="logo-modal-img" />
            <button className="logo-modal-close" onClick={() => setLogoModalOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
